import React from 'react';
import type { LocaleCode, RecordableOperation } from '@/api/models';
import {
    frequentShortcuts,
    groupOperations,
    moveHighlight,
    rowElementId,
    type PickerGroups,
    type PickerSort,
} from './commandPickerMeta';

export type UseCommandPickerResult = {
    query: string;
    onQueryChange: (query: string) => void;
    sort: PickerSort;
    onSortChange: (sort: PickerSort) => void;
    groups: PickerGroups;
    /** Top four by 90-day count. Empty while searching, and on a short list. */
    shortcuts: RecordableOperation[];
    /** Index into `groups.selectable`. Always a valid row, or 0 when there are none. */
    highlight: number;
    onHighlight: (index: number) => void;
    /** Arrow / Enter / Escape, scoped to the search field. */
    onSearchKeyDown: (event: React.KeyboardEvent) => void;
};

type UseCommandPickerArgs = {
    operations: RecordableOperation[];
    locale: LocaleCode;
    onSelect: (key: string) => void;
};

/**
 * The picker's state: what has been typed, how the list is sorted, and which row
 * the keyboard is on.
 *
 * **Arrows, Enter and Escape live on the search field, not on the list.** The
 * caret never leaves the input — that is what makes typing-then-choosing one
 * gesture instead of two — so the keys are handled where focus already is and
 * the list follows. Global accelerators are a different feature and are not
 * bound here.
 *
 * **The highlight is an index into the selectable rows only.** Unavailable rows
 * are rendered and are not reachable by any means: not by arrow, not by Enter,
 * not by click. Making them skippable-but-focusable would be a keyboard that
 * lands on things that do nothing.
 *
 * **Nothing global is bound here** (BOS-131). The picker briefly had two chords
 * — `Ctrl/⌘ + K` to focus the search field, `Ctrl/⌘ + 1–4` to take a frequent
 * operation — as `window` listeners, and they were removed with the rest of the
 * Record tab's keyboard layer. What is left is on the search field's own
 * `onKeyDown`, where focus already is; the frequent operations are a row of
 * buttons and are reached by click or by Tab.
 */
export const useCommandPicker = ({
    operations,
    locale,
    onSelect,
}: UseCommandPickerArgs): UseCommandPickerResult => {
    const [query, setQuery] = React.useState('');
    const [sort, setSort] = React.useState<PickerSort>('used');
    const [highlight, setHighlight] = React.useState(0);

    const groups = React.useMemo(
        () => groupOperations(operations, query, sort, locale),
        [locale, operations, query, sort],
    );

    const shortcuts = React.useMemo(
        () => (query.trim() ? [] : frequentShortcuts(operations)),
        [operations, query],
    );

    // The highlight is clamped against the list it indexes rather than reset on
    // every keystroke: typing a narrower query while sitting on row three should
    // land on the last row, not silently jump back to the top mid-thought. It
    // only resets when the row it pointed at is gone.
    const count = groups.selectable.length;
    const safeHighlight = count === 0 ? 0 : Math.min(highlight, count - 1);

    const onQueryChange = React.useCallback((next: string) => {
        setQuery(next);
        setHighlight(0);
    }, []);

    // Keep the highlighted row on screen. Found by id rather than by ref: the
    // rows are a list the View owns, `aria-activedescendant` already addresses
    // them this way, and threading a ref per row through a map would be the same
    // lookup with more moving parts. `nearest` so a row already in view does not
    // jump.
    React.useEffect(() => {
        const chosen = groups.selectable[safeHighlight];
        if (!chosen) {
            return;
        }
        document
            .getElementById(rowElementId(chosen.key))
            ?.scrollIntoView({ block: 'nearest' });
    }, [groups.selectable, safeHighlight]);

    const onSearchKeyDown = React.useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                // Preventing default keeps the caret still: without it the
                // browser sends it to one end of the input on every press.
                event.preventDefault();
                setHighlight((current) =>
                    moveHighlight(
                        Math.min(current, Math.max(count - 1, 0)),
                        event.key === 'ArrowDown' ? 1 : -1,
                        count,
                    ),
                );
                return;
            }
            if (event.key === 'Enter') {
                event.preventDefault();
                const chosen = groups.selectable[safeHighlight];
                if (chosen) {
                    onSelect(chosen.key);
                }
                return;
            }
            if (event.key === 'Escape') {
                event.preventDefault();
                if (query.length > 0) {
                    onQueryChange('');
                    return;
                }
                // Already empty: the second Escape leaves the field. Pressing it
                // twice is how somebody who reached the picker by keyboard gets
                // back out without the mouse.
                (event.target as HTMLElement | null)?.blur();
            }
        },
        [
            count,
            groups.selectable,
            onQueryChange,
            onSelect,
            query.length,
            safeHighlight,
        ],
    );

    return {
        query,
        onQueryChange,
        sort,
        onSortChange: setSort,
        groups,
        shortcuts,
        highlight: safeHighlight,
        onHighlight: setHighlight,
        onSearchKeyDown,
    };
};
